import type { AeTemplateDetail, AeTemplateSection } from '@/types/assessmentEngine';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import {
  DEMOGRAPHICS_SECTION_CODE,
  type AeTemplateDefaultConfig,
  type AeTemplateDimension,
  type AeTemplateSubDimension,
  templateConfigFromDetail,
} from './templateConfigTypes';
import { sectionParticipatesInScoring } from './sectionScoring';

/** Top-level scored sections for dimension sync (excludes demographics and context sections). */
export function scoringSections(sections: AeTemplateSection[]): AeTemplateSection[] {
  return sections.filter(
    (s) =>
      s.code !== DEMOGRAPHICS_SECTION_CODE &&
      !s.parent_section_id &&
      sectionParticipatesInScoring(s.scoring_config)
  );
}

function stableDimensionId(sectionId: string, existingId?: string): string {
  return existingId ?? `dim_${sectionId.replace(/-/g, '').slice(0, 12)}`;
}

function stableSubDimensionId(sectionId: string, existingId?: string): string {
  return existingId ?? `subdim_${sectionId.replace(/-/g, '').slice(0, 12)}`;
}

/**
 * Build default_config.dimensions from the section tree.
 * Top-level sections = dimensions; nested subsections = sub-dimensions.
 * Preserves existing dimension ids when section_id matches.
 */
export function buildDimensionsFromSections(
  sections: AeTemplateSection[],
  existing: AeTemplateDimension[] = []
): AeTemplateDimension[] {
  return scoringSections(sections).map((section) => {
    const existingDim = existing.find((d) => d.section_id === section.id);

    const sub_dimensions: AeTemplateSubDimension[] = (section.subsections ?? []).map((sub) => {
      const existingSub = existingDim?.sub_dimensions?.find((sd) => sd.section_id === sub.id);
      return {
        id: stableSubDimensionId(sub.id, existingSub?.id),
        name: sub.name,
        section_id: sub.id,
      };
    });

    return {
      id: stableDimensionId(section.id, existingDim?.id),
      name: section.name,
      section_id: section.id,
      sub_dimensions,
    };
  });
}

/** Merge section-derived dimensions into template default_config. */
export function mergeTemplateConfigDimensions(
  config: AeTemplateDefaultConfig,
  sections: AeTemplateSection[]
): AeTemplateDefaultConfig {
  return {
    ...config,
    dimensions: buildDimensionsFromSections(sections, config.dimensions ?? []),
  };
}

export function mergedConfigFromTemplate(template: AeTemplateDetail): AeTemplateDefaultConfig {
  return mergeTemplateConfigDimensions(templateConfigFromDetail(template), template.sections);
}

/** Persist default_config.dimensions synced from the current section tree. */
export async function persistSyncedDimensions(template: AeTemplateDetail): Promise<void> {
  const config = mergedConfigFromTemplate(template);
  await assessmentEngineService.updateTemplate(template.id, {
    default_config: config as Record<string, unknown>,
  });
}

/** Reload template and write synced dimensions to default_config. */
export async function refreshAndPersistDimensions(templateId: string): Promise<void> {
  const template = await assessmentEngineService.getTemplate(templateId);
  await persistSyncedDimensions(template);
}

/** Push dimension / sub-dimension names from settings into section records. */
export async function applyDimensionNamesToSections(
  templateId: string,
  dimensions: AeTemplateDimension[]
): Promise<void> {
  for (const dim of dimensions) {
    if (dim.section_id) {
      await assessmentEngineService.updateSection(templateId, dim.section_id, { name: dim.name });
    }
    for (const sub of dim.sub_dimensions ?? []) {
      await assessmentEngineService.updateSection(templateId, sub.section_id, { name: sub.name });
    }
  }
}
